import React, { useContext, useEffect, useMemo } from "react";
import Masonry from "react-masonry-css";
import {
    HorizontalBarChart,
    moduleNames,
    StatisticsContainer,
    UsersStats,
    useTitle,
} from "modules/common";
import {
    getAccountDashboardData,
    getModuleUsageData,
    ModuleUsageStats,
    NotificationsStats,
    RegistrationStats,
    SubscriptionsStats,
    SystemAdministrationSkeleton,
} from "modules/dashboard";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { sum, values } from "lodash";
import { ReactComponent as DownloadIcon } from "assets/icons/download.svg";
import { AccessibilityContext } from "./AccessibilityContext";
import { classNames } from "helpers/ClassNameHelper";

const getModuleLabelFromCount = (key: string) => {
    switch (key) {
        case "webTotalModuleNewsAndAdviceCount":
        case "mobileTotalModuleNewsAndAdviceCount":
            return moduleNames.NewsAndAdvice.label;
        case "webTotalModuleAgrometeorologyStationsCount":
        case "mobileTotalModuleAgrometeorologyStationsCount":
            return moduleNames.Agrometeorology.label;
        case "webTotalModuleAgroclimateStationsCount":
        case "mobileTotalModuleAgroclimateStationsCount":
            return moduleNames.AgroclimateStations.label;
        case "webTotalModulePlantProtectionCount":
        case "mobileTotalModulePlantProtectionCount":
            return moduleNames.PlantProtection.label;
        case "webTotalModuleCommunicationCount":
        case "mobileTotalModuleCommunicationCount":
            return moduleNames.Communication.label;
        case "webTotalModuleEducationCount":
        case "mobileTotalModuleEducationCount":
            return moduleNames.Education.label;
        case "webTotalModulePresentationCount":
        case "mobileTotalModulePresentationCount":
            return moduleNames.EducationDocuments.label;
        case "webTotalModuleRecordCount":
        case "mobileTotalModuleRecordCount":
            return moduleNames.Records.label;
        case "webTotalModuleMyProfileCount":
        case "mobileTotalModuleMyProfileCount":
            return moduleNames.UserProfile.label;
        default:
            return key;
    }
};

export const SystemAdministration: React.FC = () => {
    useTitle("Administracija sustava - eSavjetnik");
    const dispatch = useDispatch();
    const { data: dashboardData, isFetching: isFetchingDashboardData } = useSelector(
        (state: RootState) => state.dashboardData
    );
    const { data: moduleUsageDashboardResponse, isFetching: isFetchingModuleUsageData } =
        useSelector((state: RootState) => state.moduleUsageData);

    const moduleUsageData: {
        label: string;
        web: number;
        mobile: number;
    }[] = useMemo(() => {
        const data = moduleUsageDashboardResponse?.moduleUsageDashboardView;
        if (!data) {
            return [];
        }
        const tableData = Object.entries(data).map(([key, value]) => {
            return {
                label: getModuleLabelFromCount(key),
                web: key.includes("web") ? value : 0,
                mobile: key.includes("mobile") ? value : 0,
            };
        });

        // merge web and mobile values for the same module
        const mergedData = tableData.reduce((acc: any, curr: any) => {
            const existing = acc.find((item: any) => item.label === curr.label);
            if (existing) {
                existing.web += curr.web;
                existing.mobile += curr.mobile;
            } else {
                acc.push(curr);
            }
            return acc;
        }, []);

        return mergedData;
    }, [moduleUsageDashboardResponse]);

    const { lowVision } = useContext(AccessibilityContext);

    const tableColumns = useMemo(
        () => [
            {
                Header: "Naziv",
                accessor: "label",
                className: lowVision ? "low-vision-black" : "name-header"
            },

            {
                Header: "Web",
                accessor: (data: any) => (
                    <div className={lowVision ? "low-vision-darkgray" : "module-table-values"}>
                        {data.web}
                    </div>
                ),
                className: "web-header",
                id: 2,
            },
            {
                Header: "Mobilni",
                accessor: (data: any) => (
                    <div className={lowVision ? "low-vision-darkgray" : "module-table-values"}>
                        {data.mobile}
                    </div>
                ),
                className: "mobile-header",
                id: 3,
            },
            {
                id: "graph",
                accessor: (data: any) => <HorizontalBarChart data={[data.web, data.mobile]} />,
            },
        ],
        [lowVision]
    );

    useEffect(() => {
        dispatch(getAccountDashboardData());
        dispatch(getModuleUsageData());
    }, [dispatch]);

    const totalRegistered =
        (dashboardData?.accountDashboardRegisteredView?.totalFarmerAccountCount ?? 0) +
        (dashboardData?.accountDashboardRegisteredView?.totalCitizenAccountCount ?? 0);

    function sumObjectValues(data: unknown) {
        return sum(values(data));
    }

    return (
        <div className="system-administration">
            <div className="system-administration-header">
                <div className="system-administration-header-filters">
                    <span className="system-administration-header-text">Informacije o sustavu</span>
                </div>
                <CSVLink
                    className="btn btn--xsmall btn--primary low-vision-btn"
                    data={JSON.stringify(dashboardData)}
                    filename="eSavjetnik-podaci.csv"
                >
                    <DownloadIcon className="btn--right-icon" />
                    <span className="download-button-text">Preuzmi</span>
                </CSVLink>
            </div>

            {isFetchingDashboardData || isFetchingModuleUsageData ? (
                <SystemAdministrationSkeleton />
            ) : (
                <>
                    <Masonry
                        breakpointCols={{ default: 3, 1024: 2, 768: 1 }}
                        className="masonry-grid mt-28"
                        columnClassName="masonry-grid__column"
                    >
                        <StatisticsContainer
                            title="Broj korisnika"
                            className={lowVision ? "low-vision-darkgray" : ""}
                        >
                            <UsersStats
                                numberOfRegistered={
                                    dashboardData?.accountDashboardRegisteredView
                                        ?.totalAccountRegisteredCount ?? 0
                                }
                                numberOfSignedIn={
                                    dashboardData?.accountDashboardRegisteredView
                                        ?.totalAccountLoginCount ?? 0
                                }
                            />
                        </StatisticsContainer>
                        <RegistrationStats
                            numberOfFarmers={
                                dashboardData?.accountDashboardRegisteredView
                                    ?.totalFarmerAccountCount ?? 0
                            }
                            numberOfCitizens={
                                dashboardData?.accountDashboardRegisteredView
                                    ?.totalCitizenAccountCount ?? 0
                            }
                            total={totalRegistered}
                        />
                        <SubscriptionsStats
                            total={sumObjectValues(dashboardData?.accountDashboardInfoView)}
                            usersByAdvices={
                                dashboardData?.accountDashboardInfoView?.totalInfoAdviceCount ?? 0
                            }
                            usersByCourses={
                                dashboardData?.accountDashboardInfoView
                                    ?.totalInfoCoursesNotificationCount ?? 0
                            }
                            usersByManifestations={
                                dashboardData?.accountDashboardInfoView
                                    ?.totalInfoAnnouncementCount ?? 0
                            }
                            usersByFarmingAndOther={
                                dashboardData?.accountDashboardInfoView
                                    ?.totalInfoFarmingAndOtherCount ?? 0
                            }
                            usersByFruitAndOliveGrowing={
                                dashboardData?.accountDashboardInfoView
                                    ?.totalInfoFruitAndOliveGrowingCount ?? 0
                            }
                            usersByWineGrowing={
                                dashboardData?.accountDashboardInfoView
                                    ?.totalInfoWineGrowingCount ?? 0
                            }
                            usersByNews={
                                dashboardData?.accountDashboardInfoView?.totalInfoNewsCount ?? 0
                            }
                            usersByWeatherAlarm={
                                dashboardData?.accountDashboardInfoView
                                    ?.totalInfoWeatherAlarmCount ?? 0
                            }
                        />

                        <NotificationsStats
                            numberOfEmailUsers={
                                dashboardData?.accountDashboardNotificationView
                                    ?.totalNotificationEmailCount ?? 0
                            }
                            numberOfAppUsers={
                                dashboardData?.accountDashboardNotificationView
                                    ?.totalNotificationPushCount ?? 0
                            }
                        />
                    </Masonry>

                    <ModuleUsageStats columns={tableColumns} data={moduleUsageData} />
                </>
            )}
        </div>
    );
};
